/*
 * ROCANIIRU customization
 * File: assets/rc-mega-menu.js
 * Version: v1.2.0
 * Date: 2026-06-29
 *
 * Keeps Prestige's outer mega-menu behavior and switches mega-b's inner panels and product images.
 */

if (!customElements.get('rc-mega-menu-tabs')) {
  customElements.define(
    'rc-mega-menu-tabs',
    class RcMegaMenuTabs extends HTMLElement {
      connectedCallback() {
        if (this.isInitialized) {
          return;
        }

        this.isInitialized = true;
        this.tabs = Array.from(this.querySelectorAll('[data-rc-mega-b-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-rc-mega-b-panel]'));
        this.productTriggers = Array.from(this.querySelectorAll('[data-rc-mega-b-products]'));
        this.productTemplates = Array.from(this.querySelectorAll('[data-rc-mega-b-products-template]'));

        this.tabs.forEach((tab) => {
          const activate = () => this.activateTab(tab.dataset.rcMegaBTab, true);

          tab.addEventListener('mouseenter', activate);
          tab.addEventListener('focus', activate);
        });

        this.productTriggers.forEach((trigger) => {
          const activate = () => {
            const tabId = trigger.dataset.rcMegaBParentTab;
            const templateId = trigger.dataset.rcMegaBProducts;

            this.activateTab(tabId, false);
            this.showProducts(tabId, templateId);
          };

          trigger.addEventListener('mouseenter', activate);
          trigger.addEventListener('focus', activate);
        });

        const initialTabId = this.dataset.initialTab || this.tabs[0]?.dataset.rcMegaBTab;

        this.activateTab(initialTabId, true);

        if (this.dataset.initialProductsTemplate) {
          this.showProducts(initialTabId, this.dataset.initialProductsTemplate);
        }
      }

      activateTab(tabId, resetProducts) {
        if (!tabId) {
          return;
        }

        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.rcMegaBTab === tabId;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-expanded', String(isActive));
        });

        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.rcMegaBPanel !== tabId;
        });

        this.activeTabId = tabId;

        if (resetProducts) {
          const activePanel = this.getPanel(tabId);
          const defaultTemplateId = activePanel?.querySelector('[data-rc-mega-b-product-stage]')?.dataset.defaultTemplate;

          this.showProducts(tabId, defaultTemplateId);
        }
      }

      showProducts(tabId, templateId) {
        if (!tabId || !templateId) {
          return;
        }

        const panel = this.getPanel(tabId);
        const stage = panel?.querySelector('[data-rc-mega-b-product-stage]');
        const template = this.productTemplates.find((item) => item.dataset.rcMegaBProductsTemplate === templateId);

        if (!stage || !template || stage.dataset.activeTemplate === templateId) {
          return;
        }

        this.productTriggers.forEach((trigger) => {
          trigger.classList.toggle('is-product-active', trigger.dataset.rcMegaBProducts === templateId);
        });

        stage.replaceChildren(template.content.cloneNode(true));
        stage.dataset.activeTemplate = templateId;
        this.revealProductCards(stage, templateId);
      }

      getPanel(tabId) {
        return this.panels.find((panel) => panel.dataset.rcMegaBPanel === tabId);
      }

      async revealProductCards(stage, templateId) {
        const cards = Array.from(stage.querySelectorAll('.mega-menu-b__product-link'));

        for (const [index, card] of cards.entries()) {
          const image = card.querySelector('.mega-menu-b__product-image');

          await this.waitForImage(image);

          if (index > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, 120));
          }

          if (stage.dataset.activeTemplate !== templateId || !stage.contains(card)) {
            return;
          }

          window.requestAnimationFrame(() => card.classList.add('is-image-visible'));
        }
      }

      waitForImage(image) {
        if (!image || image.complete) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }
    }
  );
}
